<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { ColorReference } from '../types/shopfloor'

const props = defineProps<{
  open: boolean
  title?: string
  selectedId?: number | null
}>()

const emit = defineEmits<{
  close: []
  select: [color: ColorReference]
}>()

const { t } = useI18n()
const { listColorReferences } = useShopfloorApi()

const searchValue = ref('')
const items = ref<ColorReference[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)
const totalPages = ref(1)

function toOptions(list: ColorReference[]): SmartPickerOption[] {
  return list.map((color) => ({
    id: color.id,
    title: color.name,
    subtitle: color.code || t('shopfloor.knittingOutput.noColorCode'),
    meta: `#${color.id}`,
  }))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listColorReferences({
      search: searchValue.value.trim() || undefined,
      page: page.value,
      per_page: 20,
    })
    items.value = result.items
    totalPages.value = result.meta.total_pages
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function reset() {
  searchValue.value = ''
  page.value = 1
  error.value = ''
  items.value = []
}

function openModal() {
  reset()
  void load()
}

watch(() => props.open, (isOpen) => {
  if (isOpen) openModal()
})

watch(searchValue, () => {
  page.value = 1
  void load()
})

onMounted(() => {
  if (props.open) openModal()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('shopfloor.knittingOutput.colorPickerTitle')"
    :eyebrow="t('shopfloor.knittingOutput.colorPickerEyebrow')"
    :search-label="t('shopfloor.knittingOutput.colorSearch')"
    :search-placeholder="t('shopfloor.knittingOutput.colorSearchPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="t('shopfloor.knittingOutput.noColors')"
    :items="toOptions(items)"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId ?? null"
    :page-label="t('shopfloor.knittingOutput.colorPage', { page, total: Math.max(totalPages, 1) })"
    :close-aria-label="t('shopfloor.knittingOutput.closeColorPicker')"
    :retry-label="t('shopfloor.state.retry')"
    :previous-label="t('shopfloor.pagination.previous')"
    :next-label="t('shopfloor.pagination.next')"
    :cancel-label="t('shopfloor.assignments.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page = Math.max(1, page - 1); load()"
    @next="page = Math.min(totalPages, page + 1); load()"
    @select="(option: SmartPickerOption) => {
      const match = items.find((color) => String(color.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ t('shopfloor.knittingOutput.colorLoading') }}</template>
  </SmartPickerModal>
</template>
